import React, { useState, useEffect, useMemo } from 'react';
import { compose } from 'recompose';
import { useDispatch, useSelector, connect } from 'react-redux';
import withStyles from '@mui/styles/withStyles';
import { formValueSelector, change, reset } from 'redux-form';
import { getFileExtension, acceptedFileExtensions } from '../../services/utils/document-service';
import {
  UPLOAD_DOC_FILENAME_FIELD,
  UPLOAD_DOC_FILETYPE_FIELD,
  UPLOAD_DOC_FILENOTE_FIELD,
  UPLOAD_DOCUMENT_FORM,
} from '../../constants';
import { uploadFile, addDocument } from '../../services/utils/upload-document-service';
import { addNewDocument } from '../../actions/action-uploaded-documents';
import { styles } from './document-bar-styles';
import DocumentForm from './document-form';

const UploadNewDocument = props => {
  const { classes, fileName, fileTypes, fileNote } = props;
  const dispatch = useDispatch();

  const [documentLabels, setDocumentLabels] = useState([]);
  // This is used to change the display if a file is selected. Should be bool
  const [fileSelected, setFileSelected] = useState();
  // Contains the actual file that the user selected
  const [selectedFile, setSelectedFile] = useState();
  const [disableSubmit, setDisableSubmit] = useState(false);
  const [errorMessage, setErrorMessage] = useState();
  const [successMessage, setSuccessMessage] = useState();
  const [uploading, setUploading] = useState();
  const [fileExtension, setFileExtension] = useState();

  const customerId = useSelector(state => state.filters.selectedCustomerId);
  const patientId = useSelector(state => state.selectedPatientId);
  const { resourceId, tagTypeId, drugName, tagTypeLabel, order_fc_ids, therapy_ids } = useSelector(
    state => state.documentBar,
  );
  const existingTasks = useSelector(state => Object.values(state.tasks?.data || {}));
  const existingTherapies = useSelector(state => Object.values(state.therapies?.data || {}));
  const defaultDocumentTypes = useSelector(state => state.lookups.defaultDocumentLabels);
  const showTargetTherapies = Array.isArray(order_fc_ids) && order_fc_ids.length > 1;

  const [selectedTherapiesForDocument, setSelectedTherapiesForDocument] = useState([
    { value: -1, label: 'All Therapies in the Order' },
  ]);

  useEffect(() => {
    if (defaultDocumentTypes) {
      const types = defaultDocumentTypes.map(dt => ({
        label: dt.label,
        value: dt.label,
      }));
      setDocumentLabels(types);
    }
  }, [defaultDocumentTypes]);

  useEffect(() => {
    if (showTargetTherapies) {
      setSelectedTherapiesForDocument([{ value: -1, label: 'All Therapies in the Order' }]);
    }
  }, [showTargetTherapies]);

  const resetForm = () => {
    setFileSelected(false);
    setSelectedFile(undefined);
    setUploading(false);
    setErrorMessage();
    setSelectedTherapiesForDocument([{ value: -1, label: 'All Therapies in the Order' }]);
    dispatch(reset());
  };

  const therapyOptions = useMemo(() => {
    if (!showTargetTherapies || !Array.isArray(therapy_ids)) {
      return [];
    }
    const options = existingTherapies
      .filter(therapy => therapy_ids.includes(therapy.id))
      .reduce((acc, therapy) => {
        if (!acc.some(option => option.value === therapy.id)) {
          acc.push({
            value: therapy.id,
            label: `${therapy.drug_name} ${therapy.strength} ${therapy.strength_unit_of_measure} ${therapy.dosage_form}`,
          });
        }
        return acc;
      }, []);

    if (
      selectedTherapiesForDocument.some(option => option.value !== -1) ||
      selectedTherapiesForDocument.length === 0
    ) {
      options.push({ value: -1, label: 'All Therapies in the Order' });
    }

    return options;
  }, [existingTherapies, therapy_ids, selectedTherapiesForDocument, showTargetTherapies]);

  const handleTargetTherapiesChange = options => {
    let newSelection = options;
    if (!options || options.length === 0) {
      newSelection = [{ value: -1, label: 'All Therapies in the Order' }];
      return setSelectedTherapiesForDocument(newSelection);
    }
    if (
      newSelection.some(option => option.value === -1) &&
      selectedTherapiesForDocument.some(option => option.value !== -1)
    ) {
      newSelection = [{ value: -1, label: 'All Therapies in the Order' }];
      return setSelectedTherapiesForDocument(newSelection);
    }
    if (options.length > 1 && newSelection.some(option => option.value === -1)) {
      newSelection = newSelection.filter(option => option.value !== -1);
    }
    return setSelectedTherapiesForDocument(newSelection);
  };

  const buildDocumentTags = () => {
    if (!showTargetTherapies) {
      return [
        {
          tag_type_id: tagTypeId,
          resource_id: resourceId,
        },
      ];
    }

    const targetTherapies =
      selectedTherapiesForDocument.length === 1 && selectedTherapiesForDocument[0].value === -1
        ? existingTherapies.filter(therapy => therapy_ids.includes(therapy.id))
        : selectedTherapiesForDocument
            .map(option => existingTherapies.find(therapy => therapy.id === option.value))
            .filter(Boolean);

    const targetTherapyIds = targetTherapies.map(option => option.id);
    const targetFCs = existingTasks.filter(
      task => task.taskType === 'FC' && targetTherapyIds.includes(task.therapy_id),
    );

    if (targetFCs.length) {
      return targetFCs.map(task => ({ tag_type_id: tagTypeId, resource_id: task.id }));
    }

    return (order_fc_ids || []).map(id => ({ tag_type_id: tagTypeId, resource_id: id }));
  };

  const handleFileRejected = () => {
    setSuccessMessage();
    setErrorMessage('You selected an invalid file, please try again.');
  };

  const handleFileAccepted = event => {
    if (event && event.length > 0) {
      // eslint-disable-next-line no-shadow
      const { filePrefix, fileExtension } = getFileExtension(event[0].name);
      if (!acceptedFileExtensions.includes(fileExtension.toLowerCase())) {
        handleFileRejected();
      } else {
        setErrorMessage();
        setSelectedFile(event[0]);
        setFileSelected(true);
        setDisableSubmit(false);

        dispatch(change(UPLOAD_DOCUMENT_FORM, UPLOAD_DOC_FILENAME_FIELD, filePrefix));
        setFileExtension(fileExtension);
      }
    } else {
      handleFileRejected();
    }
  };

  const handleFilePaste = event => {
    const { files } = event.clipboardData;
    handleFileAccepted(files);
  };

  const deselectFile = () => {
    resetForm();
  };

  const handleSubmit = async () => {
    setDisableSubmit(true);
    setUploading(true);

    const fullFileName = `${fileName}.${fileExtension}`;

    try {
      const uploadResult = await uploadFile(selectedFile, fullFileName, customerId, patientId);
      const labels = fileTypes ? fileTypes.split(',') : [];
      const tags = buildDocumentTags();
      const response = await addDocument(
        customerId,
        patientId,
        uploadResult.uuid,
        fullFileName,
        labels,
        tags,
        fileNote,
      );
      dispatch(addNewDocument(response.data.result));
      resetForm();
      setSuccessMessage('File uploaded');
    } catch (error) {
      setUploading(false);
      setDisableSubmit(false);
      setSuccessMessage();
      setErrorMessage('Uploading your file failed. Please try again.');
    }
  };

  const tags = showTargetTherapies ? [tagTypeLabel] : [tagTypeLabel, drugName];

  return (
    <DocumentForm
      showClose
      handleClose={deselectFile}
      fileSelected={fileSelected}
      formTitle="Attachment"
      drugName={drugName}
      documentLabels={documentLabels}
      submitDisabled={disableSubmit}
      cancelDisabled={disableSubmit}
      tags={tags}
      submitButtonText="Upload file"
      formSubmit={handleSubmit}
      onCancel={deselectFile}
      errorMessage={errorMessage}
      successMessage={successMessage}
      fileName={fileName}
      fileExtension={fileExtension}
      classes={classes}
      acceptedFileExtensions={acceptedFileExtensions}
      errorCaption={`Accepted file types: ${acceptedFileExtensions}`}
      handleFilePaste={handleFilePaste}
      handleAcceptedDrop={handleFileAccepted}
      handleRejectedDrop={handleFileRejected}
      isProcessing={uploading}
      showTargetTherapies={showTargetTherapies}
      therapyOptions={therapyOptions}
      selectedTherapiesForDocument={selectedTherapiesForDocument}
      onTargetTherapiesChange={handleTargetTherapiesChange}
    />
  );
};

const mapStateToProps = state => {
  const selector = formValueSelector(UPLOAD_DOCUMENT_FORM);
  const fileName = selector(state, UPLOAD_DOC_FILENAME_FIELD);
  const fileTypes = selector(state, UPLOAD_DOC_FILETYPE_FIELD);
  const fileNote = selector(state, UPLOAD_DOC_FILENOTE_FIELD);

  return { fileName, fileTypes, fileNote };
};

export default compose(withStyles(styles), connect(mapStateToProps))(UploadNewDocument);
